/** Product-level semantics for CannaSpy's decision-oriented UI. */
export type DecisionPriority =
  | 'critical'
  | 'high'
  | 'opportunity'
  | 'watch'
  | 'healthy'
  | 'insufficient-data'

export type DecisionActionLevel =
  | 'act-now'
  | 'review'
  | 'investigate'
  | 'monitor'
  | 'resolve'

export type InsightStatus = 'new' | 'ongoing' | 'resolved' | 'dismissed'
export type DataConfidence = 'high' | 'medium' | 'low' | 'unavailable'

export interface DecisionContract {
  /** Stable route or page identifier used by the audit. */
  pageId: string
  /** The person or role making the decision. */
  user: string
  /** One question, written as a question. */
  primaryQuestion: string
  /** The conclusion the first viewport must communicate. */
  headlineAnswer: string
  /** The decision this page enables. */
  decision: string
  /** A concrete verb-led next step. */
  primaryAction: string
  /** Observable evidence that the page helped. */
  successSignal: string
}

export interface ComparisonContext {
  /** Explicit baseline, e.g. "previous 30 days" or "local market average". */
  baseline: string
  /** Optional measurement window, e.g. "Aug 12–Sep 10". */
  period?: string
}

export interface DataProvenance {
  updatedAt: string
  source?: string
  confidence?: DataConfidence
  isEstimate?: boolean
}

export const PRIORITY_LABEL: Record<DecisionPriority, string> = {
  critical: 'Critical',
  high: 'High priority',
  opportunity: 'Opportunity',
  watch: 'Watch',
  healthy: 'Healthy',
  'insufficient-data': 'Insufficient data',
}

export const ACTION_LEVEL_LABEL: Record<DecisionActionLevel, string> = {
  'act-now': 'Act now',
  review: 'Review',
  investigate: 'Investigate',
  monitor: 'Monitor',
  resolve: 'Resolve',
}

/** Lightweight guard for page-contract registries and development tooling. */
export function validateDecisionContract(contract: DecisionContract): string[] {
  const issues: string[] = []
  const fields = Object.entries(contract) as Array<[keyof DecisionContract, string]>

  for (const [key, value] of fields) {
    if (!value.trim()) issues.push(`${key} is required`)
  }
  if (contract.primaryQuestion.trim() && !contract.primaryQuestion.trim().endsWith('?')) {
    issues.push('primaryQuestion must be written as a question')
  }

  return issues
}
